import { MessageErrorType } from 'src/components/Chat/Chat.enum';
import { eChatEventErrorCodes } from 'src/providers/SocketsProvider/Sockets.enums';
import {
  UNSUPPORTED_NOTICE_SOURCES,
  tUnsupportedNotice,
} from './unsupportedNoticeAnalytics.constants';

/**
 * INT-1691 R2: content we cannot show, whoever sent it. A message the business
 * sent from the phone (WhatsApp Web) or its own echo (WhatsApp API) carries
 * these too, so they count in both directions.
 */
const UNSUPPORTED_CONTENT_ERRORS: ReadonlySet<string> = new Set([
  MessageErrorType.MESSAGE_NOT_SUPPORTED,
  MessageErrorType.PINNED_MESSAGE_NOTIFICATION_NOT_SUPPORTED,
  MessageErrorType.PLACEHOLDER_MESSAGE_NOT_SUPPORTED,
  MessageErrorType.TEMPLATE_MESSAGE_NOT_SUPPORTED,
  MessageErrorType.INTERACTIVE_MESSAGE_NOT_SUPPORTED,
  MessageErrorType.LOTTIE_STICKER_NOT_SUPPORTED,
  MessageErrorType.GROUP_MENTION_IN_STATUS_NOTIFICATION_NOT_SUPPORTED,
  MessageErrorType.GROUP_INVITE_MESSAGE_NOT_SUPPORTED,
  MessageErrorType.DEVICE_SENT_MESSAGE_NOT_SUPPORTED,
  MessageErrorType.COMMUNITY_ANNOUNCEMENT_COMMENT_NOT_SUPPORTED,
  MessageErrorType.WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR,
  MessageErrorType.WAPI_EDITED_MESSAGE_ERROR,
  MessageErrorType.WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR,
]);

/** INT-1708: the client's own Lottie notice has no error; KTD7 reports it as 2005. */
export const LOTTIE_CLIENT_NOTICE: tUnsupportedNotice = {
  code: String(eChatEventErrorCodes.LOTTIE_STICKER_NOT_SUPPORTED),
  source: UNSUPPORTED_NOTICE_SOURCES.CLIENT,
};

/**
 * INT-1691 KTD5: the chat code of an error name, as text ('1004'). Both enums
 * share their member names. A name with no chat code (an inbound error the
 * front has no case for, INT-1690) is reported as the name itself.
 */
export function chatErrorCodeOf(error: string): string {
  // A numeric enum maps both ways: only a number is a code, not the reverse
  // entry that eChatEventErrorCodes['1004'] holds.
  const code = (eChatEventErrorCodes as unknown as Record<string, unknown>)[
    error
  ];
  return typeof code === 'number' ? String(code) : error;
}

/**
 * INT-1691 KTD6: a notice the backend explained, counted unconditionally. The
 * WhatsApp API incoming notice uses it: its resolver only returns one for
 * content that did not arrive, the business's own echoes included.
 */
export function serverNoticeOf(
  error: string | null | undefined,
): tUnsupportedNotice | null {
  if (!error || error === MessageErrorType.NO_ERROR) return null;
  return {
    code: chatErrorCodeOf(error),
    source: UNSUPPORTED_NOTICE_SOURCES.SERVER,
  };
}

/**
 * INT-1691 R2/R3: the notice a bubble counts, or null. Any notice on a message
 * from the contact counts; on the business side only content we cannot show
 * does, never a failed send (credit card, Meta 5004, TikTok 4002/4003).
 */
export function countedNoticeOf(message: {
  error?: string | null;
  fromMe?: boolean;
}): tUnsupportedNotice | null {
  const { error, fromMe } = message;
  if (!error) return null;
  if (fromMe && !UNSUPPORTED_CONTENT_ERRORS.has(error)) return null;
  return serverNoticeOf(error);
}
